import { Component, OnDestroy, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { ToastrService } from 'ngx-toastr'

import { HeaderComponent } from '../common/header/header.component'
import { SidebarComponent } from '../common/slidebar/sidebar.components'
import { OverlayLoaderComponent } from '../common/loader/overlay-loader/overlay-loader.component'
import { FeedbackService } from '../../services/feedback/feedback.service'

type AttachmentKind = 'image' | 'audio' | 'video'
type FeedbackType = 'bug' | 'idea' | 'praise' | 'other'

interface LocalAttachment {
  kind: AttachmentKind
  file: File
  previewUrl: string
}

interface FeedbackTypeOption {
  value: FeedbackType
  label: string
  description: string
}

@Component({
  selector: 'app-feedback',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    HeaderComponent,
    SidebarComponent,
    OverlayLoaderComponent,
  ],
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.css',
})
export class FeedbackComponent implements OnInit, OnDestroy {
  isLoading = false

  message = ''
  rating = 0
  hoverRating = 0
  feedbackType: FeedbackType | null = null
  replyEmail = ''
  attachments: LocalAttachment[] = []

  readonly maxMessageLength = 1000
  readonly feedbackTypes: FeedbackTypeOption[] = [
    { value: 'bug', label: 'Bug', description: 'Something is broken' },
    { value: 'idea', label: 'Idea', description: 'A feature or improvement' },
    { value: 'praise', label: 'Praise', description: 'Tell us what you love' },
    { value: 'other', label: 'Other', description: 'Anything else' },
  ]

  private readonly maxImages = 10
  private readonly maxAudio = 5
  private readonly maxVideo = 5
  private readonly maxTotal = 10

  /* Voice recording temporarily disabled
  private readonly maxRecordingSeconds = 300

  isRecording = false
  recordingSeconds = 0

  private mediaStream: MediaStream | null = null
  private mediaRecorder: MediaRecorder | null = null
  private recordingChunks: Blob[] = []
  private recordingTimer: ReturnType<typeof setInterval> | null = null
  private recordingMimeType = 'audio/webm'
  */

  constructor(
    private feedbackService: FeedbackService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    // this.cleanupRecording()
    for (const a of this.attachments) URL.revokeObjectURL(a.previewUrl)
  }

  /* Voice recording temporarily disabled
  formatRecordingTime(): string {
    const minutes = Math.floor(this.recordingSeconds / 60)
    const seconds = this.recordingSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  async startRecording(): Promise<void> {
    if (this.isLoading || this.isRecording) return

    if (!this.canAdd('audio', 1)) {
      this.toastr.error('Too many audio attachments. Please remove one before recording.')
      return
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      this.toastr.error('Voice recording is not supported in this browser.')
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      this.mediaStream = stream
      this.recordingMimeType = this.getSupportedRecordingMimeType()
      this.recordingChunks = []

      const recorder = new MediaRecorder(stream, { mimeType: this.recordingMimeType })
      this.mediaRecorder = recorder

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) this.recordingChunks.push(event.data)
      }

      recorder.onstop = () => {
        const mimeBase = this.recordingMimeType.split(';')[0]
        const blob = new Blob(this.recordingChunks, { type: mimeBase })
        if (blob.size === 0) {
          this.toastr.error('Recording was empty. Please try again.')
          this.cleanupRecording()
          return
        }

        const ext = mimeBase.includes('ogg') ? 'ogg' : 'webm'
        const fileName = `voice-recording-${Date.now()}.${ext}`
        const file = new File([blob], fileName, { type: mimeBase })
        const previewUrl = URL.createObjectURL(blob)
        this.attachments.push({ kind: 'audio', file, previewUrl })
        this.toastr.success('Voice recording saved. You can preview it below.')
        this.cleanupRecording()
      }

      recorder.start(250)
      this.isRecording = true
      this.recordingSeconds = 0
      this.recordingTimer = setInterval(() => {
        this.recordingSeconds += 1
        if (this.recordingSeconds >= this.maxRecordingSeconds) {
          this.stopRecording()
          this.toastr.info('Recording stopped at the 5 minute limit.')
        }
      }, 1000)
    } catch {
      this.cleanupRecording()
      this.toastr.error('Microphone access denied. Please allow permission and try again.')
    }
  }

  stopRecording(): void {
    if (!this.isRecording || !this.mediaRecorder) return

    this.isRecording = false
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer)
      this.recordingTimer = null
    }

    if (this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop()
    }
  }

  private getSupportedRecordingMimeType(): string {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
    ]
    for (const type of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
        return type
      }
    }
    return 'audio/webm'
  }

  private cleanupRecording(): void {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer)
      this.recordingTimer = null
    }

    this.mediaStream?.getTracks().forEach((track) => track.stop())
    this.mediaStream = null
    this.mediaRecorder = null
    this.recordingChunks = []
    this.isRecording = false
    this.recordingSeconds = 0
  }
  */

  get charactersLeft(): number {
    return this.maxMessageLength - this.message.length
  }

  private countKind(kind: AttachmentKind): number {
    return this.attachments.filter((a) => a.kind === kind).length
  }

  private canAdd(kind: AttachmentKind, incomingCount: number): boolean {
    const newKindCount = this.countKind(kind) + incomingCount
    const newTotal = this.attachments.length + incomingCount
    if (kind === 'image' && newKindCount > this.maxImages) return false
    if (kind === 'audio' && newKindCount > this.maxAudio) return false
    if (kind === 'video' && newKindCount > this.maxVideo) return false
    if (newTotal > this.maxTotal) return false
    return true
  }

  onImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files || [])
    if (files.length === 0) return

    if (!this.canAdd('image', files.length)) {
      this.toastr.error('Too many attachments. Please reduce image uploads.')
      return
    }

    for (const f of files) {
      const previewUrl = URL.createObjectURL(f)
      this.attachments.push({ kind: 'image', file: f, previewUrl })
    }
    input.value = ''
  }

  /* Voice / Audio temporarily disabled
  onAudioSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files || [])
    if (files.length === 0) return

    if (!this.canAdd('audio', files.length)) {
      this.toastr.error('Too many attachments. Please reduce audio uploads.')
      return
    }

    for (const f of files) {
      const previewUrl = URL.createObjectURL(f)
      this.attachments.push({ kind: 'audio', file: f, previewUrl })
    }
    input.value = ''
  }
  */

  onVideoSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files || [])
    if (files.length === 0) return

    if (!this.canAdd('video', files.length)) {
      this.toastr.error('Too many attachments. Please reduce video uploads.')
      return
    }

    for (const f of files) {
      const previewUrl = URL.createObjectURL(f)
      this.attachments.push({ kind: 'video', file: f, previewUrl })
    }
    input.value = ''
  }

  removeAttachment(index: number): void {
    const a = this.attachments[index]
    if (!a) return
    URL.revokeObjectURL(a.previewUrl)
    this.attachments.splice(index, 1)
  }

  setRating(value: number): void {
    if (this.isLoading) return
    this.rating = value
  }

  onStarHover(value: number): void {
    if (this.isLoading) return
    this.hoverRating = value
  }

  onStarLeave(): void {
    this.hoverRating = 0
  }

  isStarActive(star: number): boolean {
    const active = this.hoverRating || this.rating
    return star <= active
  }

  get activeRatingValue(): number {
    return this.hoverRating || this.rating
  }

  get ratingLabelText(): string {
    switch (this.activeRatingValue) {
      case 1:
        return 'Terrible'
      case 2:
        return 'Poor'
      case 3:
        return 'Okay'
      case 4:
        return 'Good'
      case 5:
        return 'Excellent'
      default:
        return 'Tap to rate'
    }
  }

  setFeedbackType(value: FeedbackType): void {
    if (this.isLoading) return
    this.feedbackType = value
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  private clearForm(): void {
    // this.cleanupRecording()
    for (const a of this.attachments) URL.revokeObjectURL(a.previewUrl)
    this.attachments = []
    this.message = ''
    this.rating = 0
    this.hoverRating = 0
    this.feedbackType = null
    this.replyEmail = ''
  }

  submit(): void {
    /* Voice recording temporarily disabled
    if (this.isRecording) {
      this.toastr.error('Please stop the voice recording before submitting.')
      return
    }
    */

    const trimmed = this.message.trim()
    if (!trimmed) {
      this.toastr.error('Please enter your feedback message.')
      return
    }

    if (trimmed.length > this.maxMessageLength) {
      this.toastr.error(`Message is too long (max ${this.maxMessageLength} characters).`)
      return
    }

    if (!this.feedbackType) {
      this.toastr.error('Please select a feedback type.')
      return
    }

    const reply = this.replyEmail.trim()
    if (reply && !this.isValidEmail(reply)) {
      this.toastr.error('Please enter a valid reply email address.')
      return
    }

    this.isLoading = true
    const formData = new FormData()
    formData.append('message', trimmed)
    formData.append('feedback_type', this.feedbackType)

    if (this.rating >= 1 && this.rating <= 5) {
      formData.append('rating', String(this.rating))
    }

    if (reply) {
      formData.append('reply_email', reply)
    }

    for (const a of this.attachments) {
      if (a.kind === 'image') formData.append('images', a.file, a.file.name)
      // if (a.kind === 'audio') formData.append('audio', a.file, a.file.name)
      if (a.kind === 'video') formData.append('video', a.file, a.file.name)
    }

    this.feedbackService.submitFeedback(formData).subscribe({
      next: () => {
        this.toastr.success('Feedback submitted successfully.')
        this.clearForm()
        this.isLoading = false
      },
      error: (error) => {
        this.isLoading = false
        const msg =
          error?.error?.message_key ||
          error?.error?.message ||
          'Failed to submit feedback.'
        this.toastr.error(msg)
      },
    })
  }
}
